import { useCallback } from "react"
import { useApolloClient, gql } from "@apollo/client"
import { useCore } from "@app/hooks/useCore"
import { useCartContext } from "@app/providers/cart"

import { VARIANT_FRAGMENT } from "@app/graphql/fragments/variantFragment"

const GET_PRODUCT_VARIANTS = gql`
  query GetProductVariants($handle: String!, $countryCode: CountryCode!, $firstVariants: Int!) @inContext(country: $countryCode) {
    product(handle: $handle) {
      id
      handle
      variants(first: $firstVariants) {
        edges {
          node {
            ...VariantFragment
          }
        }
      }
    }
  }
  ${VARIANT_FRAGMENT}
`

type UseShopifyProductVariantsReturn = {
  fetchProductVariants: (handle: string) => Promise<any[]>
}

export const useShopifyProductVariants = (): UseShopifyProductVariantsReturn => {
  const client = useApolloClient()
  const {
    helpers: { edgeNormaliser },
  } = useCore()
  const { countryCode } = useCartContext()

  const fetchProductVariants = useCallback(
    async (handle: string): Promise<any[]> => {
      if (!handle) {
        return []
      }

      try {
        const { data } = await client.query({
          query: GET_PRODUCT_VARIANTS,
          variables: {
            handle,
            countryCode: countryCode || "AU",
            firstVariants: 10,
          },
        })

        const variants = edgeNormaliser(data?.product?.variants?.edges) || []
        return variants
      } catch (error) {
        console.error("Failed to fetch product variants:", error)
        return []
      }
    },
    [client, edgeNormaliser, countryCode]
  )

  return { fetchProductVariants }
}
